import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useServerEventListener } from '@bonusly/channels';
import { Box, Button, DownloadOutline, HStack, rem, Spinner, Text, useToast } from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';

import { LeaderboardFilters } from '../../../bonus-feed/features/recognition/api/hooks/leaderboards';

interface LeaderboardExportBarProps {
	totalCount: number;
	filters: Omit<LeaderboardFilters, 'limit' | 'offset'>;
	isFetching?: boolean;
}

const EXPORT_MAX_LIMIT = 50_000;
const EXPORT_TIMEOUT_MS = 30 * 1000;
const FILE_TYPE = 'leaderboard_export';

// The leaderboard CSV endpoint is async: it returns `{ generated_file_id }` with
// 202 Accepted, then publishes a `CsvGenerationComplete` WebSocket event when
// the file is ready. The global `AppEvents` listener handles the actual file
// download; this component listens for the same event only to release the
// button's spinning state once the matching file arrives. If the user closes
// the tab before the event arrives, the backend emails them the link via
// `Jobs::Reporting::EmailGeneratedFile` scheduled 1 minute later.
export const LeaderboardExportBar: FC<LeaderboardExportBarProps> = ({ totalCount, filters, isFetching = false }) => {
	const { t } = useTranslation('navigation');
	const [isExporting, setIsExporting] = useState(false);
	// Refs mirror state so the websocket callback (captured at subscription
	// time) can read the current value without restale closures.
	const isExportingRef = useRef(false);
	const activeFileIdRef = useRef<string | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const http = useHttpClient('v2');
	const toast = useToast();

	const filtersRef = useRef(filters);
	filtersRef.current = filters;
	const totalCountRef = useRef(totalCount);
	totalCountRef.current = totalCount;

	const resetExporting = useCallback(() => {
		isExportingRef.current = false;
		activeFileIdRef.current = null;
		setIsExporting(false);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	useServerEventListener(
		'CsvGenerationComplete',
		useCallback(
			(data: { file_id: string; file_type: string }) => {
				if (!isExportingRef.current) return;
				if (data.file_type !== FILE_TYPE) return;
				// Require a non-null id match. Until the 202 response lands and
				// we capture our own generated_file_id, any leaderboard_export
				// event arriving (e.g. from another tab's earlier export) would
				// otherwise pass this guard and reset our spinner prematurely.
				if (!activeFileIdRef.current || data.file_id !== activeFileIdRef.current) return;
				resetExporting();
			},
			[resetExporting]
		)
	);

	// The backend doesn't currently broadcast CsvGenerationFailed for
	// leaderboard_export (the job re-raises and Sidekiq retries), but listen
	// defensively so the button releases immediately with a real error toast
	// if that ever changes — instead of waiting out the 30s timeout and
	// showing the softer "still preparing" message.
	useServerEventListener(
		'CsvGenerationFailed',
		useCallback(
			(data: { file_id: string; file_type: string }) => {
				if (!isExportingRef.current) return;
				if (data.file_type !== FILE_TYPE) return;
				if (!activeFileIdRef.current || data.file_id !== activeFileIdRef.current) return;
				toast({ title: t('leaderboard.exportError'), status: 'error', duration: 4000 });
				resetExporting();
			},
			[toast, t, resetExporting]
		)
	);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	const handleExport = useCallback(async () => {
		if (isExportingRef.current) return;

		const currentFilters = filtersRef.current;
		const currentTotalCount = totalCountRef.current;
		isExportingRef.current = true;
		setIsExporting(true);

		try {
			const params: Record<string, string> = {};
			if (currentFilters.start_date) params.start_date = currentFilters.start_date;
			if (currentFilters.end_date) params.end_date = currentFilters.end_date;
			if (currentFilters.direction) params.direction = currentFilters.direction;
			if (currentFilters.hashtag) params.hashtag = currentFilters.hashtag;
			if (currentFilters.team) params.team = currentFilters.team;
			if (currentFilters.department) params.department = currentFilters.department;

			const response = await http.get<{ generated_file_id: string }>('leaderboards/export_csv', { params });
			activeFileIdRef.current = response.data.generated_file_id;

			toast({
				title: t('leaderboard.exportStarting'),
				description: t('leaderboard.exportStartingDescription'),
				status: 'success',
				duration: 5000,
			});

			if (currentTotalCount > EXPORT_MAX_LIMIT) {
				toast({
					title: t('leaderboard.exportTruncated', { exported: EXPORT_MAX_LIMIT.toLocaleString(), total: currentTotalCount.toLocaleString() }),
					status: 'info',
					duration: 6000,
				});
			}

			// Safety net for the case the websocket never fires (dropped
			// connection, backend hung, etc.). Typical exports complete in
			// 2-5s — by 30s the websocket is the likely suspect, but the
			// export job itself is probably still running, so don't claim it
			// failed. Show an info toast pointing the user at the email
			// fallback and release the button so they can retry if needed.
			timeoutRef.current = setTimeout(() => {
				if (!isExportingRef.current) return;
				toast({
					title: t('leaderboard.exportSlow'),
					description: t('leaderboard.exportSlowDescription'),
					status: 'info',
					duration: 6000,
				});
				resetExporting();
			}, EXPORT_TIMEOUT_MS);
		} catch {
			toast({ title: t('leaderboard.exportError'), status: 'error', duration: 4000 });
			resetExporting();
		}
	}, [http, t, toast, resetExporting]);

	return (
		<Box>
			<Box mx={rem(-8)} borderTop="1px solid" borderColor="border.soft" />
			<HStack justify="space-between" align="center" px={rem(20)} py={rem(10)}>
				<Text fontSize="sm" color="text.softest">
					{t('leaderboard.resultCount', { count: totalCount })}
				</Text>
				<Button
					variant="ghost"
					colorScheme="primary"
					size="xs"
					onClick={handleExport}
					isDisabled={isExporting || isFetching}
					leftIcon={isExporting ? <Spinner size="xs" /> : <DownloadOutline boxSize={rem(16)} />}
				>
					{t('leaderboard.exportCsv')}
				</Button>
			</HStack>
		</Box>
	);
};
